/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/require-await -- the mock implements an async
   (Promise-returning) interface synchronously; keeping the methods `async` turns their
   guard-clause throws into promise rejections, exactly as the real API interceptor does. */

import { addMinutes } from "date-fns"
import { MedaicaApiErrorType } from "@medaica/common/services/medaica-api-service"
import { PatientReportFacts, PatientReportSession, PatientReportVerifyResult } from "@medaica/common/types"
import { PatientReportApiClient } from "./patient-report-api-client"

/**
 * In-memory mock of the patient report API, so the download page is demoable and
 * testable with no backend. Errors are shaped like the real response interceptor's
 * output (`isMedaicaApiError` + numeric `type`, plus the `attemptsRemaining` /
 * `reason` passthroughs and the HTTP status the page reads for a 410) so the
 * page's error handling is identical against mock and real.
 *
 * Demo triggers: link token "demo" resolves to a report that comes with a copy for the
 * patient's doctor; "demo-own-report-only" resolves to one without; "expired" is a dead
 * link (expired); "revoked" is a dead link (report no longer available); any other token
 * reads as expired, like the real API. Code "123456" verifies; "000000" reads as an expired
 * code; anything else is wrong and locks after 5 attempts.
 */
type MockApiError = Error & {
  isMedaicaApiError: true
  type: MedaicaApiErrorType
  response?: { status: number }
}

const mockApiError = (type: MedaicaApiErrorType, extra?: Record<string, unknown>): MockApiError => {
  const err = new Error(MedaicaApiErrorType[type]) as MockApiError
  err.isMedaicaApiError = true
  err.type = type
  if (extra) {
    Object.assign(err, extra)
  }
  return err
}

const DEMO_TOKEN = "demo"
const OWN_REPORT_ONLY_TOKEN = "demo-own-report-only"
const EXPIRED_TOKEN = "expired"
const REVOKED_TOKEN = "revoked"
const CORRECT_CODE = "123456"
const EXPIRED_CODE = "000000"
const MAX_CODE_ATTEMPTS = 5
const SESSION_MINUTES = 15
const CODE_MINUTES = 10

const MOCK_FACTS: PatientReportFacts = {
  patientName: "Anna Johnson",
  examDate: "Aug 27, 2026",
  releasedAt: new Date("2026-09-01T15:30:00Z"),
  filename: "Medaica-Report-Anna-Johnson-2026-08-27.pdf",
  providerReport: { filename: "Medaica-Report-for-your-doctor-2026-08-27.pdf" },
}

class MockPatientReportApi implements PatientReportApiClient {
  private readonly maskedEmail = "a•••@example.com"
  private readonly verificationToken = "mock-verification-token"
  private codeExpiresAt: Date = addMinutes(new Date(), CODE_MINUTES)
  private codeAttempts = 0
  private codeUsed = false
  private sessionExpiresAt: Date | null = null
  private releaseRevoked = false
  private hasProviderReport = true

  // --- gate (pre-session) ---

  async createSession(token: string): Promise<PatientReportSession> {
    if (token === REVOKED_TOKEN) {
      throw mockApiError(MedaicaApiErrorType.INVALID_REPORT_LINK, { reason: "unavailable" })
    }
    if (token === EXPIRED_TOKEN || (token !== DEMO_TOKEN && token !== OWN_REPORT_ONLY_TOKEN)) {
      throw mockApiError(MedaicaApiErrorType.INVALID_REPORT_LINK, { reason: "expired" })
    }
    this.hasProviderReport = token === DEMO_TOKEN
    this.codeExpiresAt = addMinutes(new Date(), CODE_MINUTES)
    this.codeAttempts = 0
    this.codeUsed = false
    return {
      maskedEmail: this.maskedEmail,
      codeExpiresAt: this.codeExpiresAt,
      verificationToken: this.verificationToken,
    }
  }

  async verify(_verificationToken: string, code: string): Promise<PatientReportVerifyResult> {
    if (this.codeUsed || new Date() > this.codeExpiresAt || code === EXPIRED_CODE) {
      throw mockApiError(MedaicaApiErrorType.CODE_EXPIRED)
    }
    if (this.codeAttempts >= MAX_CODE_ATTEMPTS) {
      throw mockApiError(MedaicaApiErrorType.CODE_LOCKED)
    }
    if (code === CORRECT_CODE) {
      this.codeUsed = true
      this.sessionExpiresAt = addMinutes(new Date(), SESSION_MINUTES)
      return { accessToken: "mock-report-session-token", expiresIn: SESSION_MINUTES * 60 }
    }
    this.codeAttempts += 1
    if (this.codeAttempts >= MAX_CODE_ATTEMPTS) {
      throw mockApiError(MedaicaApiErrorType.CODE_LOCKED)
    }
    throw mockApiError(MedaicaApiErrorType.INVALID_DATA, { attemptsRemaining: MAX_CODE_ATTEMPTS - this.codeAttempts })
  }

  // A resend mints a fresh code on the same handle and resets the attempt count. Once the
  // code has been used there is nothing to resend — the page starts over from the landing.
  async resendCode(_verificationToken: string): Promise<{ codeExpiresAt: Date }> {
    if (this.codeUsed) {
      throw mockApiError(MedaicaApiErrorType.CODE_EXPIRED)
    }
    this.codeExpiresAt = addMinutes(new Date(), CODE_MINUTES)
    this.codeAttempts = 0
    return { codeExpiresAt: this.codeExpiresAt }
  }

  // --- session-scoped ---

  private assertSession(): void {
    if (!this.sessionExpiresAt || new Date() > this.sessionExpiresAt) {
      throw mockApiError(MedaicaApiErrorType.SIGNATURE_HAS_EXPIRED, { response: { status: 401 } })
    }
    if (this.releaseRevoked) {
      throw mockApiError(MedaicaApiErrorType.INVALID_REPORT_LINK, { reason: "unavailable", response: { status: 410 } })
    }
  }

  async getFacts(): Promise<PatientReportFacts> {
    this.assertSession()
    return { ...MOCK_FACTS, providerReport: this.hasProviderReport ? MOCK_FACTS.providerReport : null }
  }

  async getPdf(): Promise<Blob> {
    this.assertSession()
    return new Blob(["%PDF-stub"], { type: "application/pdf" })
  }

  async getProviderPdf(): Promise<Blob> {
    this.assertSession()
    if (!this.hasProviderReport) {
      throw mockApiError(MedaicaApiErrorType.RESOURCE_DOES_NOT_EXIST, { response: { status: 404 } })
    }
    return new Blob(["%PDF-provider-stub"], { type: "application/pdf" })
  }

  // --- test/demo helpers ---

  /** Force the download session's expiry, so the next session call is a 401. */
  expireSession(): void {
    this.sessionExpiresAt = new Date(Date.now() - 1000)
  }

  /** Simulate the care team reverting the release mid-session: session calls become a 410. */
  revokeRelease(): void {
    this.releaseRevoked = true
  }
}

export default MockPatientReportApi
